import { useState } from 'react';
import {
  Grid,
  Button,
  CircularProgress,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Dialog,
  List,
  ListItem,
  ListItemText,
  Link,
} from '@material-ui/core';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  SpikeAnnotations,
  DeleteEntity,
} from '@ciandtspike.io/plugin-ciandtspike-common';
import {
  useApi,
  alertApiRef,
  discoveryApiRef,
  fetchApiRef,
  useRouteRef,
} from '@backstage/core-plugin-api';
import { provisionedResourcesIndexRouteRef } from '@ciandtspike.io/plugin-ciandtspike-marketplace';
import { useNavigate } from 'react-router-dom';
import {
  Entity,
  EntityRelation,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityDeletePermission } from '@backstage/plugin-catalog-common/alpha';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { ciandtspikeDeleteEntityTranslationRef } from '../../translation';

export const DeleteEntityComponent = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(ciandtspikeDeleteEntityTranslationRef);
  const alertApi = useApi(alertApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const navigate = useNavigate();
  const provisionedResourcesLink = useRouteRef(provisionedResourcesIndexRouteRef);
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [relationBlockOpen, setRelationBlockOpen] = useState(false);
  const [relatedEntityTypes, setRelatedEntityTypes] = useState<
    Record<string, string>
  >({});

  const parsedSource = DeleteEntity.parseSourceAnnotation(
    entity.metadata.annotations?.[SpikeAnnotations.SOURCE],
  );
  let filePath: string | undefined = parsedSource?.filePath;
  if (!filePath && entity.kind === 'Domain') {
    filePath = ['domains', `${entity.metadata.name}.yaml`].join('/');
  } else if (!filePath && entity.kind === 'Component') {
    filePath = entity.metadata.annotations?.[
      'backstage.io/source-location'
    ]?.replace('url:', '');
  }

  // Check for blocking relations
  const blockingRelations =
    entity.relations?.filter(
      (relation: EntityRelation) =>
        relation.type === 'hasPart' ||
        relation.type === 'parentOf' ||
        relation.type === 'dependencyOf',
    ) || [];

  const hasBlockingRelations = blockingRelations.length > 0;

  const handleDeleteClick = async () => {
    if (hasBlockingRelations) {
      const types: Record<string, string> = {};
      await Promise.all(
        blockingRelations.map(async (relation: EntityRelation) => {
          try {
            const related = (await catalogApi.getEntityByRef(
              relation.targetRef,
            )) as Entity | undefined;
            types[relation.targetRef] = (related?.spec as any)?.type || '';
          } catch {
            // ignore fetch errors
          }
        }),
      );
      setRelatedEntityTypes(types);
      setRelationBlockOpen(true);
    } else {
      setOpen(true);
    }
  };

  const handleCancel = () => setOpen(false);
  const handleRelationBlockCancel = () => setRelationBlockOpen(false);

  const handleProceed = async () => {
    setIsDeleting(true);
    try {
      if (filePath) {
        const response = await DeleteEntity.callDeleteEntityEndpoint(
          discoveryApi,
          fetchApi,
          {
            filePath,
            entityRef: `${entity.kind}:${
              entity.metadata.namespace || 'default'
            }/${entity.metadata.name}`,
            entityKind: entity.kind,
          },
        );

        if (response.status !== 204) {
          let reason = response.statusText;
          try {
            const body = await response.json();
            if (body?.error?.message) {
              reason = body.error.message;
            }
          } catch {
            // Corpo da resposta não é JSON (ex: erro de infraestrutura antes
            // de chegar ao error-handler do Backstage) — mantém statusText.
          }
          throw new Error(`${response.status}: ${reason}`);
        }

        await catalogApi.removeEntityByUid(entity.metadata.uid!);

        alertApi.post({
          message: 'Entity deleted and unregistered successfully.',
          severity: 'success',
        });
        navigate(provisionedResourcesLink());
      } else {
        alertApi.post({
          message: 'Missing Bitbucket or entity information.',
          severity: 'error',
        });
      }
    } catch (error) {
      alertApi.post({
        message: `${t('error.deletionFailed')}: ${String(error)}`,
        severity: 'error',
      });
    } finally {
      setIsDeleting(false);
      setOpen(false);
    }
  };

  if (filePath) {
    return (
      <RequirePermission
        permission={catalogEntityDeletePermission}
        resourceRef={stringifyEntityRef(entity)}
      >
        <Grid item md={12}>
          <Button
            variant="contained"
            color="secondary"
            style={{ marginTop: 0 }}
            onClick={handleDeleteClick}
          >
            Delete Entity
          </Button>

          {/* Deletion confirmation dialog */}
          <Dialog open={open} onClose={isDeleting ? undefined : handleCancel}>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              <Typography>
                This action is <b>irreversible</b>. All resources provided by
                Crossplane for this entity will be excluded from the cloud
                provider and the entity will be unregistered from the catalog.
                Are you sure you want to proceed?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancel} color="default" disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                onClick={handleProceed}
                color="secondary"
                variant="contained"
                disabled={isDeleting}
                startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
              >
                Proceed
              </Button>
            </DialogActions>
          </Dialog>

          {/* Relations blocking dialog */}
          <Dialog
            open={relationBlockOpen}
            onClose={handleRelationBlockCancel}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Cannot Delete Entity</DialogTitle>
            <DialogContent>
              <Typography paragraph>
                This entity cannot be deleted because it has the following
                relations that depend on it:
              </Typography>
              <List>
                {blockingRelations.map((relation, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={
                        <Link
                          href={provisionedResourcesLink()}
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none' }}
                        >
                          {(() => {
                            const ref = parseEntityRef(relation.targetRef);
                            const type = relatedEntityTypes[relation.targetRef];
                            const kindLabel = type
                              ? `${ref.kind}(${type})`
                              : ref.kind;
                            return `${kindLabel}:${
                              ref.namespace || 'default'
                            }/${ref.name}`;
                          })()}
                        </Link>
                      }
                      secondary={`Relation type: ${relation.type}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography>
                Please remove these relations first before attempting to delete
                this entity.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleRelationBlockCancel} color="primary">
                OK
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </RequirePermission>
    );
  }

  return <></>;
};
